import React, {
  Fragment,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import type {ReactElement, Ref} from 'react';

import {graphql, usePaginationFragment} from 'react-relay';

import type {UserListRow_user$key} from '../../../graphql/__generated__/UserListRow_user.graphql';
import type {UserListTableRowsPaginationQuery} from '../../../graphql/__generated__/UserListTableRowsPaginationQuery.graphql';
import type {UserListTableRows_admin$key} from '../../../graphql/__generated__/UserListTableRows_admin.graphql';

import UserListRow from './UserListRow';
import type {Filters} from './user-filter';

export interface RefetchRef {
  refetchData: (type?: Filters, value?: string) => void;
  isArchived: boolean;
  setIsArchived: (isArchived: boolean) => void;
}

type Props = {
  admin: UserListTableRows_admin$key;
  currentUserId?: string;
};

const DEFAULT_IS_ARCHIVED = false;

const Rows = (props: Props, ref: Ref<RefetchRef>): ReactElement => {
  const {admin, currentUserId} = props;
  const [isArchived, setIsArchived] = useState(DEFAULT_IS_ARCHIVED);
  const isArchivedRef = useRef(isArchived);

  const {data, refetch} = usePaginationFragment<
    UserListTableRowsPaginationQuery,
    UserListTableRows_admin$key
  >(
    graphql`
      fragment UserListTableRows_admin on Query
      @argumentDefinitions(
        count: {type: "Int", defaultValue: null}
        cursor: {type: "String", defaultValue: null}
        email: {type: "String", defaultValue: null}
        fName: {type: "String", defaultValue: null}
        lName: {type: "String", defaultValue: null}
        isArchived: {type: "Boolean", defaultValue: false}
        isDeleted: {type: "Boolean", defaultValue: false}
      )
      @refetchable(queryName: "UserListTableRowsPaginationQuery") {
        admin {
          users(
            query: {
              filter: {
                email: {includes: $email}
                firstName: {includes: $fName}
                lastName: {includes: $lName}
                isArchived: $isArchived
                isDeleted: $isDeleted
              }
              sort: CREATED
              sortDir: DESC
            }
            first: $count
            after: $cursor
          ) @connection(key: "UserListTableRows_admin_users") {
            edges {
              node {
                id
                ...UserListRow_user
              }
            }
          }
        }
      }
    `,
    admin,
  );

  // Keep ref in sync with state
  isArchivedRef.current = isArchived;

  const refetchData = useCallback((type?: Filters, value?: string) => {
    switch (type ?? '') {
      case 'email':
        refetch(
          {email: value, isArchived: isArchivedRef.current, isDeleted: false},
          {fetchPolicy: 'network-only'},
        );
        break;
      case 'fName':
        refetch(
          {fName: value, isArchived: isArchivedRef.current, isDeleted: false},
          {fetchPolicy: 'network-only'},
        );
        break;
      case 'lName':
        refetch(
          {lName: value, isArchived: isArchivedRef.current, isDeleted: false},
          {fetchPolicy: 'network-only'},
        );
        break;
      default:
        refetch(
          {isArchived: isArchivedRef.current, isDeleted: false},
          {fetchPolicy: 'network-only'},
        );
    }
  }, []);

  const handleSetIsArchived = useCallback(
    (newIsArchived: boolean) => {
      setIsArchived(newIsArchived);
      refetch({isArchived: newIsArchived, isDeleted: false}, {fetchPolicy: 'network-only'});
    },
    [refetch],
  );

  useImperativeHandle(
    ref,
    () => ({
      isArchived,
      refetchData,
      setIsArchived: handleSetIsArchived,
    }),
    [isArchived, refetchData, handleSetIsArchived],
  );

  const rows = data?.admin?.users?.edges?.map((e) => e?.node).filter(Boolean) as Array<
    UserListRow_user$key & {id: string}
  >;

  return (
    <Fragment>
      {rows.map((user) => (
        <UserListRow
          currentUserId={currentUserId}
          isArchived={isArchived}
          key={user.id}
          refetch={(vars, options) =>
            refetch(
              {isArchived: vars?.isArchived ?? isArchivedRef.current, isDeleted: false},
              options as Parameters<typeof refetch>[1],
            )
          }
          user={user}
        />
      ))}
    </Fragment>
  );
};

const UserListTableRows = forwardRef(Rows);

export default UserListTableRows;
